import React, { useState, useRef, useCallback } from 'react';
import { 
  Bold, 
  Italic, 
  Underline, 
  Strikethrough,
  AlignLeft,
  AlignCenter,
  AlignRight,
  List,
  ListOrdered,
  Link,
  Image,
  Video,
  Paperclip,
  Smile,
  Eye,
  Edit,
  Undo,
  Redo,
  Type,
  Palette
} from 'lucide-react';
import { Button } from './button';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Separator } from './separator';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Input } from './input';
import { Label } from './label';
import { Textarea } from './textarea';
import { Switch } from './switch';
import { useToast } from './use-toast';

interface RichTextEditorProps {
  value: string;
  onChange: (content: string) => void;
  placeholder?: string;
  className?: string;
  maxLength?: number;
  disabled?: boolean;
  showPreview?: boolean;
}

interface EditorToolbarProps {
  onCommand: (command: string, value?: string) => void;
  onYouTubeEmbed: (url: string) => void;
  disabled?: boolean;
}

const emojis = [
  '😀', '😃', '😄', '😁', '😅', '😂', '🤣', '😊', '😇', '🙂',
  '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛',
  '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏',
  '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫',
  '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳',
  '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭',
  '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧',
  '👍', '👎', '👌', '✌️', '🤞', '🤟', '🤘', '🤙', '👈', '👉',
  '👆', '🖕', '👇', '☝️', '👋', '🤚', '🖐️', '✋', '🖖', '👏',
  '🙌', '🤝', '🙏', '✍️', '💪', '🦾', '🦿', '🦵', '🦶', '👂',
  '🎉', '🎊', '🎈', '🎁', '🏆', '🏅', '🥇', '🥈', '🥉', '⭐',
  '🌟', '💫', '✨', '🔥', '💥', '💢', '💦', '💨', '🕳️', '💬',
  '👑', '💎', '💰', '💸', '💳', '🎯', '🎪', '🎨', '🎭', '🎪'
];

const EditorToolbar: React.FC<EditorToolbarProps> = ({ 
  onCommand, 
  onYouTubeEmbed,
  disabled 
}) => {
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showYouTubeDialog, setShowYouTubeDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkText, setLinkText] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');


  const formatButtons = [
    { icon: Bold, command: 'bold', tooltip: 'Negrito (Ctrl+B)' },
    { icon: Italic, command: 'italic', tooltip: 'Itálico (Ctrl+I)' },
    { icon: Underline, command: 'underline', tooltip: 'Sublinhado (Ctrl+U)' },
    { icon: Strikethrough, command: 'strikethrough', tooltip: 'Riscado' },
  ];

  const alignButtons = [
    { icon: AlignLeft, command: 'justifyLeft', tooltip: 'Alinhar à esquerda' },
    { icon: AlignCenter, command: 'justifyCenter', tooltip: 'Centralizar' },
    { icon: AlignRight, command: 'justifyRight', tooltip: 'Alinhar à direita' },
  ];

  const listButtons = [
    { icon: List, command: 'insertUnorderedList', tooltip: 'Lista com marcadores' },
    { icon: ListOrdered, command: 'insertOrderedList', tooltip: 'Lista numerada' },
  ];



  const handleLinkInsert = () => {
    if (linkUrl.trim()) {
      const text = linkText.trim() || linkUrl;
      onCommand('insertHTML', `<a href="${linkUrl}" target="_blank">${text}</a>`);
      setLinkUrl('');
      setLinkText('');
      setShowLinkDialog(false);
    }
  };

  const handleYouTubeEmbed = () => {
    if (youtubeUrl.trim()) {
      onYouTubeEmbed(youtubeUrl);
      setYoutubeUrl('');
      setShowYouTubeDialog(false);
    }
  };

  const handleEmojiClick = (emoji: string) => {
    onCommand('insertText', emoji);
  };

  return (
    <div className="border-b border-[#222A35] p-2 sm:p-3 space-y-2 sm:space-y-3">
      {/* Primeira linha - Formatação básica */}
      <div className="flex items-center gap-1 flex-wrap">
        {formatButtons.map(({ icon: Icon, command, tooltip }) => (
          <Button
            key={command}
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCommand(command);
            }}
            disabled={disabled}
            title={tooltip}
            className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#222A35] text-gray-300"
          >
            <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        ))}

        <Separator orientation="vertical" className="h-5 sm:h-6 mx-1 bg-[#222A35]" />

        {/* Cores de texto */}
        <Popover>
          <PopoverTrigger asChild>
                      <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={disabled}
            className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#222A35] text-gray-300"
            title="Cor do texto"
          >
            <Palette className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
          </PopoverTrigger>
          <PopoverContent className="w-40 sm:w-48 bg-[#1A2333] border-[#222A35]">
            <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
              {['#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', 
                '#00FFFF', '#FFFFFF', '#808080', '#FFA500', '#800080', '#008080'].map(color => (
                <button
                  key={color}
                  type="button"
                  className="w-5 h-5 sm:w-6 sm:h-6 rounded border border-gray-600"
                  style={{ backgroundColor: color }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCommand('foreColor', color);
                  }}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <Separator orientation="vertical" className="h-5 sm:h-6 mx-1 bg-[#222A35]" />

        {/* Alinhamento */}
        {alignButtons.map(({ icon: Icon, command, tooltip }) => (
          <Button
            key={command}
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCommand(command);
            }}
            disabled={disabled}
            title={tooltip}
            className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#222A35] text-gray-300"
          >
            <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        ))}

        <Separator orientation="vertical" className="h-5 sm:h-6 mx-1 bg-[#222A35]" />

        {/* Listas */}
        {listButtons.map(({ icon: Icon, command, tooltip }) => (
          <Button
            key={command}
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onCommand(command);
            }}
            disabled={disabled}
            title={tooltip}
            className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#222A35] text-gray-300"
          >
            <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
          </Button>
        ))}
      </div>

      {/* Segunda linha - Mídia e extras */}
      <div className="flex items-center gap-1 flex-wrap">
        {/* Link */}
        <Popover open={showLinkDialog} onOpenChange={setShowLinkDialog}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#222A35] text-gray-300"
              title="Inserir link"
            >
              <Link className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-xs sm:w-80 bg-[#1A2333] border-[#222A35]">
            <div className="space-y-3">
              <div>
                <Label htmlFor="linkUrl" className="text-white text-sm">URL</Label>
                <Input
                  id="linkUrl"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://..."
                  className="bg-[#0F1419] border-[#222A35] text-white text-sm"
                />
              </div>
              <div>
                <Label htmlFor="linkText" className="text-white text-sm">Texto (opcional)</Label>
                <Input
                  id="linkText"
                  value={linkText}
                  onChange={(e) => setLinkText(e.target.value)}
                  placeholder="Texto do link"
                  className="bg-[#0F1419] border-[#222A35] text-white text-sm"
                />
              </div>
              <Button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleLinkInsert();
                }} 
                className="w-full bg-[#00B6F0] text-black text-sm"
              >
                Inserir Link
              </Button>
            </div>
          </PopoverContent>
        </Popover>



        {/* YouTube */}
        <Popover open={showYouTubeDialog} onOpenChange={setShowYouTubeDialog}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#222A35] text-gray-300"
              title="Inserir vídeo do YouTube"
            >
              <Video className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-full max-w-xs sm:w-80 bg-[#1A2333] border-[#222A35]">
            <div className="space-y-3">
              <div>
                <Label htmlFor="youtubeUrl" className="text-white text-sm">URL do YouTube</Label>
                <Input
                  id="youtubeUrl"
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=..."
                  className="bg-[#0F1419] border-[#222A35] text-white text-sm"
                />
              </div>
              <Button 
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleYouTubeEmbed();
                }} 
                className="w-full bg-[#00B6F0] text-black text-sm"
              >
                Inserir Vídeo
              </Button>
            </div>
          </PopoverContent>
        </Popover>



        <Separator orientation="vertical" className="h-5 sm:h-6 mx-1 bg-[#222A35]" />

        {/* Emojis */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disabled}
              className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#222A35] text-gray-300"
              title="Inserir emoji"
            >
              <Smile className="h-3 w-3 sm:h-4 sm:w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-72 sm:w-80 bg-[#1A2333] border-[#222A35]">
            <div className="grid grid-cols-8 sm:grid-cols-10 gap-1 max-h-40 sm:max-h-48 overflow-y-auto">
              {emojis.map((emoji, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEmojiClick(emoji);
                  }}
                  className="w-6 h-6 text-sm hover:bg-[#222A35] rounded transition-colors flex items-center justify-center"
                >
                  {emoji}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>

        <Separator orientation="vertical" className="h-5 sm:h-6 mx-1 bg-[#222A35]" />

        {/* Undo/Redo */}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCommand('undo');
          }}
          disabled={disabled}
          className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#222A35] text-gray-300"
          title="Desfazer (Ctrl+Z)"
        >
          <Undo className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onCommand('redo');
          }}
          disabled={disabled}
          className="h-7 w-7 sm:h-8 sm:w-8 p-0 hover:bg-[#222A35] text-gray-300"
          title="Refazer (Ctrl+Y)"
        >
          <Redo className="h-3 w-3 sm:h-4 sm:w-4" />
        </Button>
      </div>
    </div>
  );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Digite seu conteúdo...",
  className = "",
  maxLength,
  disabled = false,
  showPreview = true
}) => {
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [currentLength, setCurrentLength] = useState(0);
  const editorRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();

  // Sincronizar o conteúdo inicial uma vez
  const [isInitialized, setIsInitialized] = useState(false);

  React.useEffect(() => {
    if (editorRef.current) {
      // Sincronizar sempre que o value mudar, mas apenas se for diferente do conteúdo atual
      const currentContent = editorRef.current.innerHTML;
      if (value !== currentContent) {
        editorRef.current.innerHTML = value;
        setCurrentLength(editorRef.current.textContent?.length || 0);
      }
      setIsInitialized(true);
    }
  }, [value]);

  // Sincronizar conteúdo quando sair do modo preview
  React.useEffect(() => {
    if (!isPreviewMode && editorRef.current && value && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value;
      setCurrentLength(editorRef.current.textContent?.length || 0);
    }
  }, [isPreviewMode, value]);

  const executeCommand = useCallback((command: string, value?: string) => {
    if (disabled) return;
    
    // Focar o editor antes de executar o comando
    if (editorRef.current) {
      editorRef.current.focus();
    }
    
    document.execCommand(command, false, value);
    
    // Atualizar o conteúdo
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      onChange(content);
      setCurrentLength(editorRef.current.textContent?.length || 0);
    }
  }, [disabled, onChange]);

  const handleImageUpload = useCallback(async (file: File) => {
    // Orientar o usuário a usar o campo de upload do formulário
    toast({
      title: "Use o campo de upload acima",
      description: "Para adicionar imagens, use o botão 'Hospedar Imagem em Nossos Servidores' no formulário acima. Isso garante que a imagem ficará sempre disponível.",
      variant: "destructive"
    });
  }, [toast]);

  const handleYouTubeEmbed = useCallback((url: string) => {
    const getYouTubeVideoId = (url: string) => {
      const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
      const match = url.match(regExp);
      return match && match[7].length === 11 ? match[7] : null;
    };

    const videoId = getYouTubeVideoId(url);
    if (videoId) {
      const embedHtml = `
        <div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; max-width: 100%; margin: 15px 0; border: 1px solid #333; border-radius: 8px; z-index: 1;">
          <iframe 
            src="https://www.youtube.com/embed/${videoId}" 
            style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 8px; z-index: 1;" 
            frameborder="0" 
            allowfullscreen
            tabindex="-1">
          </iframe>
        </div>
        <p><br></p>
        <p>Continue digitando aqui...</p>
        <p><br></p>
      `;
      executeCommand('insertHTML', embedHtml);
      
      // Focar no editor após inserir o vídeo
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.focus();
          // Mover cursor para o final
          const range = document.createRange();
          const selection = window.getSelection();
          range.selectNodeContents(editorRef.current);
          range.collapse(false);
          selection?.removeAllRanges();
          selection?.addRange(range);
        }
      }, 100);
    } else {
      toast({
        title: "URL inválida",
        description: "Por favor, insira uma URL válida do YouTube",
        variant: "destructive"
      });
    }
  }, [executeCommand, toast]);



  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const content = editorRef.current.innerHTML;
      const textLength = editorRef.current.textContent?.length || 0;
      
      if (maxLength && textLength > maxLength) {
        toast({
          title: "Limite de caracteres excedido",
          description: `Máximo de ${maxLength} caracteres permitidos`,
          variant: "destructive"
        });
        return;
      }
      
      onChange(content);
      setCurrentLength(textLength);
    }
  }, [onChange, maxLength, toast]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    
    // Verificar se há imagens sendo coladas
    const items = Array.from(e.clipboardData.items);
    const hasImage = items.some(item => item.type.startsWith('image/'));
    
    if (hasImage) {
      toast({
        title: "Imagem não pode ser colada",
        description: "Para adicionar imagens, use o campo 'Hospedar Imagem' no formulário acima do editor.",
        variant: "destructive"
      });
      return;
    }
    
    // Permitir apenas texto
    const text = e.clipboardData.getData('text/plain');
    if (text) {
      executeCommand('insertText', text);
    }
  }, [executeCommand, toast]);

  // Limpar editor quando value for vazio (para resetar formulário)
  React.useEffect(() => {
    if (!value && editorRef.current && editorRef.current.innerHTML !== '') {
      editorRef.current.innerHTML = '';
      setCurrentLength(0);
      setIsInitialized(false);
    }
  }, [value]);

  return (
    <Card className={`bg-[#1A2333] border-[#222A35] ${className}`}>
      <CardHeader className="pb-2 px-3 sm:px-6">
        <div className="flex items-center justify-between">
          <CardTitle className="text-white text-sm sm:text-base">Editor de Conteúdo</CardTitle>
          {showPreview && (
            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                type="button"
                variant={!isPreviewMode ? "outline" : "ghost"}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsPreviewMode(false);
                }}
                className="h-6 px-2 sm:h-7 sm:px-2 text-xs"
              >
                <Edit className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">Editar</span>
              </Button>
              <Button
                type="button"
                variant={isPreviewMode ? "outline" : "ghost"}
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsPreviewMode(true);
                }}
                className="h-6 px-2 sm:h-7 sm:px-2 text-xs"
              >
                <Eye className="h-3 w-3 mr-1" />
                <span className="hidden sm:inline">Preview</span>
              </Button>
            </div>
          )}
        </div>
        
        {/* Dica de uso */}
        <div className="mt-2 p-2 bg-blue-900/20 border border-blue-700/30 rounded-md text-xs text-blue-200">
          💡 <strong>Dica:</strong> Use este editor para formatação de texto (negrito, itálico, listas, links, vídeos do YouTube, etc). Para imagens, use o campo "Hospedar Imagem" acima do editor.
        </div>
      </CardHeader>
      
      <CardContent className="p-0">
        {!isPreviewMode && (
          <EditorToolbar
            onCommand={executeCommand}
            onYouTubeEmbed={handleYouTubeEmbed}
            disabled={disabled}
          />
        )}
        
        <div className="p-3 sm:p-4">
          {isPreviewMode ? (
            <div 
              className="min-h-[150px] sm:min-h-[200px] prose prose-invert max-w-none text-white text-sm sm:text-base"
              dangerouslySetInnerHTML={{ __html: value || '<p class="text-gray-400">Nenhum conteúdo para visualizar</p>' }}
            />
          ) : (
            <>
              <style>
                {`
                  [contenteditable]:empty:before {
                    content: attr(data-placeholder);
                    color: #9CA3AF;
                    pointer-events: none;
                  }
                  
                  /* Garantir que dialogs e popovers fiquem acima de iframes */
                  [role="dialog"],
                  [data-radix-popper-content-wrapper],
                  .popover-content {
                    z-index: 9999 !important;
                  }
                  
                  /* Estilo para elementos inseridos no editor */
                  .richtexteditor-content iframe {
                    z-index: 1;
                    position: relative;
                  }
                `}
              </style>
              <div
                ref={editorRef}
                contentEditable={!disabled}
                onInput={handleInput}
                onPaste={handlePaste}
                className={`
                  min-h-[150px] sm:min-h-[200px] max-h-[300px] sm:max-h-[400px] overflow-y-auto 
                  p-2 sm:p-3 rounded 
                  bg-[#0F1419] border border-[#222A35] text-white text-sm sm:text-base
                  focus:outline-none focus:ring-2 focus:ring-[#00B6F0] focus:border-transparent
                  prose prose-invert max-w-none richtexteditor-content
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                data-placeholder={placeholder}
                style={{
                  caretColor: '#00B6F0'
                }}
                suppressContentEditableWarning={true}
              />
            </>
          )}
          
          {maxLength && !isPreviewMode && (
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mt-2 text-xs text-gray-400 gap-2">
              <span className="text-xs sm:text-sm">Use a barra de ferramentas para formatar seu conteúdo</span>
              <span className={`text-xs sm:text-sm ${currentLength > maxLength ? 'text-red-400' : ''}`}>
                {currentLength}/{maxLength}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RichTextEditor; 